/**
 * Mobile Joystick Component
 * Virtual joystick for mobile movement control
 */
import React, { useState, useRef, useCallback } from 'react';

const MobileJoystick = ({ onMove, onRelease, size = 120 }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const joystickRef = useRef(null);
  const maxDistance = size / 2 - 20;

  const handleStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMove = useCallback((e) => {
    if (!isDragging || !joystickRef.current) return;
    e.preventDefault();

    const rect = joystickRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    let dx = clientX - centerX;
    let dy = clientY - centerY;

    // Limit to max distance
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > maxDistance) {
      dx = (dx / distance) * maxDistance;
      dy = (dy / distance) * maxDistance;
    }

    setPosition({ x: dx, y: dy });

    // Normalize and send movement
    const normalizedX = dx / maxDistance;
    const normalizedY = dy / maxDistance;
    onMove?.(normalizedX, normalizedY);
  }, [isDragging, maxDistance, onMove]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
    onRelease?.();
  }, [onRelease]);

  return (
    <div
      ref={joystickRef}
      className="relative touch-none select-none"
      style={{ width: size, height: size }}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
    >
      {/* Outer ring */}
      <div 
        className="absolute inset-0 rounded-full border-2 border-white/30 bg-black/40 backdrop-blur-sm"
        style={{ boxShadow: 'inset 0 0 20px rgba(147, 51, 234, 0.3)' }}
      />
      
      {/* Direction indicators */}
      <div className="absolute inset-4 flex items-center justify-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 text-white/20 text-xs">▲</div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-white/20 text-xs">▼</div>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 text-white/20 text-xs">◀</div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 text-white/20 text-xs">▶</div>
      </div>
      
      {/* Inner knob */}
      <div
        className="absolute rounded-full bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg transition-transform duration-75"
        style={{
          width: 40,
          height: 40,
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          boxShadow: isDragging 
            ? '0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.3)' 
            : '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        <div className="absolute inset-1 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 opacity-60" />
      </div>
    </div>
  );
};

export default MobileJoystick;
